import * as React from 'react';
import {SearchKeywordsCache, SearchKeywordsCacheContext, SearchableListGroup, AppLogo, Cell, ListGroupItem, LineBreak} from '@openfin/desktop-ui';

import {Entity, SearchEngine, App, AppStatusStates} from '../../../types/commons';
import {ClassNameBuilder} from '../../utils/ClassNameBuilder';
import {ApplicationEntry} from '../../components/ApplicationEntry/ApplicationEntry';
import {EntityDetailTile} from '../../components/EntityDetailTile/EntityDetailTile';
import {AppStatus} from '../../../store/partials/Directory';

import {NoResults} from './SearchMessage';
import * as Styles from './LauncherSearchResults.module.scss';

interface Props {
    search: string;
    apps: App[];
    searchEngines: SearchEngine[];
    appStatuses: AppStatus;
    setSearchEngine: (searchEngine: SearchEngine | null) => void;
    setSearch: (search: string) => void;
    cache: SearchKeywordsCacheContext;
    keyboardEventSource: React.MutableRefObject<HTMLElement | null>;
}

export const LauncherSearchResults: React.FC<Props> = (props) => {
    const {search, apps, searchEngines, appStatuses, setSearchEngine, setSearch, cache, keyboardEventSource} = props;
    const [entityToDetail, setEntityToDetail] = React.useState<Entity>(apps[0]);
    const [resultsCount, setResultsCount] = React.useState<number>(0);
    const editingSearchEngine = search.startsWith('+');
    const className = new ClassNameBuilder(Styles, 'search-lists', ['hide', resultsCount === 0]);

    const handleListUpdate = (results: Entity[], checkDetailPane: boolean) => {
        setResultsCount(results.length);
        if (checkDetailPane) {
            if (!entityToDetail || !results.find((item) => item.name === entityToDetail.name)) {
                // Detail pane is displaying an item that is no longer available. Reset to first item in the list
                setEntityToDetail(results[0]);
            }
        }
    };
    const engineSearchTextsFactory = (entity: Entity) => [entity.name, (entity.title || entity.name || ''), (entity.description || '')];
    const appSearchTextFactory = (app: App) => [
        app.name,
        (app.title || app.name || ''),
        (app.description || ''),
        (app.tags ? app.tags.map((tag) => tag.name).join(' ') : '')
    ];
    const handleSearchEngineSelect = (engine: SearchEngine) => {
        setSearchEngine(engine);
        setSearch('');
    };

    return (
        <SearchKeywordsCache.Provider value={cache}>
            <div className={className.toString()}>
                {editingSearchEngine &&
                    <SearchableListGroup<SearchEngine> onListUpdate={(results) => handleListUpdate(results, editingSearchEngine)}
                        search={search}
                        searchTextsFactory={engineSearchTextsFactory}
                        focusElement={keyboardEventSource}
                        firstItemActive
                        border={false}
                        changeResetsActiveItem
                    >
                        <LineBreak>Search Engines</LineBreak>
                        {searchEngines.map((engine, index) => {
                            return (
                                <ListGroupItem key={engine.name + index} item={engine} onSelect={handleSearchEngineSelect} onHighlight={setEntityToDetail}>
                                    <Cell text={engine.title} image={<AppLogo src={engine.icon} />} />
                                </ListGroupItem>
                            );
                        })}
                    </SearchableListGroup>
                }

                {!editingSearchEngine &&
                    <SearchableListGroup onListUpdate={(results) => handleListUpdate(results, !editingSearchEngine)}
                        search={search}
                        searchTextsFactory={appSearchTextFactory}
                        firstItemActive
                        changeResetsActiveItem
                        border={false}
                        focusElement={keyboardEventSource}
                    >
                        <LineBreak>Applications</LineBreak>
                        {apps.map((app) => (
                            <ListGroupItem key={app.id} item={app} onHighlight={setEntityToDetail}>
                                <ApplicationEntry
                                    item={app}
                                    status={appStatuses[app.id] || AppStatusStates.Closed}
                                    key={app.id} />
                            </ListGroupItem>
                        ))}
                    </SearchableListGroup>
                }
            </div>
            {resultsCount > 0
                ? <div className={Styles['details-pane']}>
                    <EntityDetailTile
                        entity={entityToDetail}
                        emptyDescription={`There are no results for "${search}".`}
                    />
                </div>
                : <NoResults />
            }
        </SearchKeywordsCache.Provider>
    );
};
